import { useAppDispatch } from '@hooks/reduxHooks'
import {
  doCreateComment,
  doUpdateComment,
} from '@state/reducers/CommentReducer'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { UpdateOrderDto } from '@/services/dtos'
import { useEffect } from 'react'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { DialogTitle } from '../titles'
import { useIntl } from 'react-intl'

interface Props {
  editingComment: UpdateOrderDto['comment'] | null
  orderId?: number
  open: boolean
  onClose: () => void
}

const commentSchema = yup.object({
  comment: yup.string().trim().min(1, 'Le commentaire est requis'),
})

const CommentForm = ({ open, onClose, orderId, editingComment }: Props) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    resolver: yupResolver(commentSchema),
    defaultValues: {
      comment: editingComment?.content,
    },
  })

  useEffect(() => {
    if (editingComment) {
      reset({
        comment: editingComment.content,
      })
      return
    }
    reset({
      comment: '',
    })
  }, [editingComment, reset])

  const onSubmit = async (data: yup.InferType<typeof commentSchema>) => {
    if (!data.comment || !orderId) {
      return
    }

    try {
      if (editingComment && editingComment.id) {
        await dispatch(
          doUpdateComment({
            id: editingComment.id,
            dto: {
              content: data.comment,
            },
          }),
        ).unwrap()

        dispatch(
          enqueueSnackbar({
            message: intl.formatMessage({ id: 'member-comment-updated' }),
            type: 'success',
          }),
        )
      } else {
        await dispatch(
          doCreateComment({
            orderId: orderId,
            content: data.comment,
          }),
        ).unwrap()

        dispatch(
          enqueueSnackbar({
            message: intl.formatMessage({ id: 'member-comment-added' }),
            type: 'success',
          }),
        )

        reset()
      }

      onClose()
    } catch (error) {
      console.error('Error adding comment:', error)
      dispatch(
        enqueueSnackbar({
          message: intl.formatMessage({ id: 'error-adding-comment' }),
          type: 'error',
        }),
      )
    }
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        title={
          editingComment ? 'Modifier le commentaire' : 'Ajouter un commentaire'
        }
        onClose={onClose}
        style={{
          background: '#fff',
          color: '#000',
          paddingLeft: '24px',
          marginBottom: '16px',
        }}
        format
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <TextField
            autoFocus
            id="comment"
            label="Commentaire"
            type="textarea"
            fullWidth
            multiline
            rows={4}
            {...register('comment')}
            error={!!errors.comment}
            helperText={errors.comment?.message}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="contained" type="submit" disabled={!isDirty}>
            {editingComment ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default CommentForm
